import axios from 'axios';
import KeycloakService from '../security/KeycloakService';
import { toast } from 'ui/extensions/notify';

declare module 'axios' {
	interface AxiosRequestConfig {
		/** El llamador avisa del error por su cuenta: el interceptor no muestra el aviso rojo. */
		skipErrorToast?: boolean;
	}
}

const API = import.meta.env.VITE_API_URL;

const apiInstance = axios.create({ baseURL: API });

// Request interceptor to add the bearer token
apiInstance.interceptors.request.use(
	async (config) => {
		// Do not add token for login-related URLs or if it's a public endpoint
		if (config.url?.includes('/auth/login')) {
			return config;
		}

		const keycloak = KeycloakService.getKeycloakInstance();
		const GRACE_PERIOD_SECONDS = 10;

		// Grace period check: If the token was issued very recently, skip the update check.
		if (keycloak.idTokenParsed?.auth_time) {
			const authTime = keycloak.idTokenParsed.auth_time;
			const currentTime = Math.floor(Date.now() / 1000);

			if (currentTime - authTime < GRACE_PERIOD_SECONDS) {
				console.log('Auth grace period: skipping token refresh.');
				const token = keycloak.token;
				if (token && config.headers) {
					config.headers.Authorization = `Bearer ${token}`;
				}
				return config;
			}
		}

		try {
			// Ensure token is fresh before the request
			await KeycloakService.updateToken(30); // Refresh if token expires in 30 seconds

			const token = KeycloakService.getToken();
			if (token && config.headers) {
				config.headers.Authorization = `Bearer ${token}`;
			}
		} catch (error) {
			console.error('Failed to refresh token; logging out.', error);
			KeycloakService.logout();
			return Promise.reject(error);
		}

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// Response interceptor for handling errors
apiInstance.interceptors.response.use(
	(response) => {
		// Any status code that lie within the range of 2xx cause this function to trigger
		return response;
	},
	(error) => {
		if (axios.isAxiosError(error) && error.config?.skipErrorToast) {
			console.error('[API Error] sin aviso del interceptor:', error.response?.status ?? error.message);
			return Promise.reject(error);
		}

		// RFC 9457: Problem Details for HTTP APIs
		if (axios.isAxiosError(error)) {
			if (error.response) {
				const problem = error.response.data;
				const status = error.response.status;

				// Standard RFC 9457 fields
				const title = problem?.title || `Error ${status}`;
				const detail = problem?.detail || problem?.message || error.response.statusText;
				const errors = problem?.errors; // Extension for field-level errors

				console.error(`[API Error] ${title} (${status}):`, problem);

				// Handle specific validation errors (mapped in backend as array of objects)
				if (Array.isArray(errors) && errors.length > 0) {
					// Group validation messages to avoid toast-spamming if they are many
					if (errors.length > 3) {
						toast.error(`${title}: ${detail}`);
					} else {
						errors.forEach((err: any) => {
							const fieldLabel = err.field ? `${err.field}: ` : '';
							toast.error(`${fieldLabel}${err.message}`);
						});
					}
				} else if (errors && typeof errors === 'object') {
					// Legacy fallback for object-based errors
					Object.entries(errors).forEach(([field, messages]) => {
						const msg = Array.isArray(messages) ? messages[0] : messages;
						toast.error(`${field}: ${msg}`);
					});
				} else {
					// Standard detail message
					toast.error(`${title}: ${detail}`);
				}
			} else if (error.request) {
				console.error('API No Response:', error.request);
				toast.error('Error de conexión: El servidor no responde.');
			} else {
				console.error('Axios Setup Error:', error.message);
				toast.error('Error de configuración en la solicitud.');
			}
		} else {
			console.error('Unexpected Error:', error);
			toast.error('Ocurrió un error inesperado.');
		}

		return Promise.reject(error);
	}
);

export default apiInstance;
